/* eslint-disable max-nested-callbacks */
import { groupBy, uniqBy } from 'lodash'
import PropTypes from 'prop-types'
import React, { useMemo, useCallback } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import Card from '../../../components/card/Card'
import { PARAMETER_NATURES } from '../../../referencial/components/parameter/constants/ParameterConstants'
import { getLabel } from '../../../utils/StoreUtils'
import { hasValue } from 'utils/NumberUtil'
import useActions from 'utils/customHook/useActions'
import { lightBlue } from 'utils/constants/ColorTheme'
import DtoOperation from 'quality/dto/operation/DtoOperation'
import { Grid } from '@mui/material'
import DtoQualityThreshold from 'quality/dto/QualityThreshold/DtoQualityThreshold'
import { CONFORMITY_OVERVIEW_EXPORT_HEADERS, calculateDataSample, calculateDataAnalysis, getAEPoverviewExportData, getConformityOverviewExportRows, getConformityOverviewRows, CONFORMITY_COLORS } from '../../utils/conformityExportUtils'

const CustomTable = ({
    referenceThreshold,
    limitThreshold,
    label = '',
    producerIds = [],
    microbioData = {},
    pcData = {},
}) => {
    const {
        producers,
        thresholds,
    } = useSelector(store => ({
        producers: store.ContributorReducer.producers,
        thresholds: store.QualityReducer.thresholds,
    }), shallowEqual)

    const nbColThreshold = !!hasValue(referenceThreshold) + !!hasValue(limitThreshold)

    return (
        <table>
            <thead>
                <tr>
                    {hasValue(referenceThreshold) && <th rowSpan='2' className='center-align width-10 aepTableCell'>{getLabel(thresholds, referenceThreshold)}</th>}
                    {hasValue(limitThreshold) && <th rowSpan='2' className='center-align width-10 aepTableCell'>{getLabel(thresholds, limitThreshold)}</th>}
                    {
                        producerIds.map(id => (<th key={id} colSpan={3} className='center-align width-10 aepTableCell' >{getLabel(producers, id, 'mnemonique', 'id') || i18n.unknownProducer}</th>))
                    }
                </tr>
                <tr>
                    {
                        producerIds.map((id) => (
                            <React.Fragment key={id}>
                                <td className='center-align width-10 aepTableCell padding-1' >&nbsp;&nbsp;{label}&nbsp;&nbsp;</td>
                                <td className='center-align width-10 aepTableCell padding-1' >&nbsp;&nbsp;{i18n.nonConform}&nbsp;&nbsp;</td>
                                <td className='center-align width-10 aepTableCell padding-1' >&nbsp;&nbsp;{i18n.conformity}&nbsp;&nbsp;</td>
                            </React.Fragment>
                        ))
                    }
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td colSpan={nbColThreshold} className='center-align aepTableCell' >{i18n.microbiological}</td>
                    {
                        producerIds.map(id => {
                            const key = `${id}`
                            return (
                                <React.Fragment key={id}>
                                    <td className='center-align aepTableCell'>{microbioData[key]?.nbTotal ?? ''}</td>
                                    <td className={`center-align aepTableCell ${microbioData[key]?.nbNonConform ? 'red' : ''}`}>{microbioData[key]?.nbNonConform ?? ''}</td>
                                    <td className='center-align aepTableCell'>{microbioData[key]?.percent ?? ''}</td>
                                </React.Fragment>
                            )
                        })
                    }
                </tr>
                <tr>
                    <td colSpan={nbColThreshold} className='center-align aepTableCell' >{i18n.physicochemic}</td>
                    {
                        producerIds.map(id => {
                            const key = `${id}`
                            return (
                                <React.Fragment key={id}>
                                    <td className='center-align aepTableCell'>{pcData[key]?.nbTotal ?? ''}</td>
                                    <td className={`center-align aepTableCell ${pcData[key]?.nbNonConform ? 'red' : ''}`}>{pcData[key]?.nbNonConform ?? ''}</td>
                                    <td className='center-align aepTableCell'>{pcData[key]?.percent ?? ''}</td>
                                </React.Fragment>
                            )
                        })
                    }
                </tr>
            </tbody>
        </table>
    )
}

CustomTable.propTypes = {
    label: PropTypes.string,
    producerIds: PropTypes.arrayOf(PropTypes.number),
    microbioData: PropTypes.objectOf(PropTypes.shape({
        nbData: PropTypes.number,
        nbNonConform: PropTypes.number,
        percent: PropTypes.string,
    })),
    pcData: PropTypes.objectOf(PropTypes.shape({
        nbData: PropTypes.number,
        nbNonConform: PropTypes.number,
        percent: PropTypes.string,
    })),
    referenceThreshold: PropTypes.number,
    limitThreshold: PropTypes.number,
}

export const NATURES_MICROBIO = [PARAMETER_NATURES.MICROBIOLOGICAL]
export const NATURES_PC = [PARAMETER_NATURES.CHEMICAL, PARAMETER_NATURES.PHYSICAL]

export const AEP_OVERVIEW_EXPORT_HEADERS = ['year', 'nature', 'producer', 'nbSample', 'nonConformPLV', 'plvConformity', 'nbAnalysis', 'nonConformAnalysis', 'analysisConformity']

const AEPoverviewPanel = ({
    filter,
    onExport,
    thresholdList = [],
    operations = [],
    analysis = [],
}) => {
    const {
        producers,
        parametersIndex,
    } = useSelector(store => ({
        producers: store.ContributorReducer.producers,
        parametersIndex: store.ParameterReducer.parametersIndex,
    }), shallowEqual)

    const formatedAnalysis = useMemo(() => {
        return analysis.filter(a => thresholdList.some(t => !t.parameterCode || t.parameterCode === a.parameter))
    }, [analysis, thresholdList])

    const producerIds = useMemo(() => {
        if (hasValue(filter.producer)) {
            return [filter.producer]
        }
        return uniqBy(analysis, 'producer').map(a => a.producer)
    }, [analysis, filter.producer])

    const groupAnalysis = useMemo(() => {
        return groupBy(formatedAnalysis, a => `${a.qualitometer}:${a.operation}`)
    }, [formatedAnalysis])

    const groupOperationsProducer = useMemo(() => {
        return groupBy(operations, a => a.producer)
    }, [operations])

    useActions(() => ({
        exportList: [{
            onClick: () => {
                const data = getAEPoverviewExportData({ analysis, operations, thresholdList, filter, producers, parametersIndex })
                onExport({
                    data: data.length ? [{ ...data[0], headers: AEP_OVERVIEW_EXPORT_HEADERS }, ...data.slice(1)] : [],
                    exportType: 'xlsx',
                    titleFile: i18n.AEPoverview,
                })
            },
            label: i18n.excel,
        }],
    }), [analysis, operations, thresholdList, filter, producers, parametersIndex, onExport])

    const formatDataSample = useCallback(natures => {
        return producerIds.reduce((acc, id) => {
            const {
                [id]: listOperations = [],
            } = groupOperationsProducer
            acc[`${id}`] = calculateDataSample(listOperations, natures, groupAnalysis, parametersIndex)
            return acc
        }, {})
    }, [producerIds, groupOperationsProducer, groupAnalysis, parametersIndex])

    const microbioDataSample = useMemo(() => formatDataSample(NATURES_MICROBIO), [formatDataSample])

    const pcDataSample = useMemo(() => formatDataSample(NATURES_PC), [formatDataSample])

    const formatDataAnalysis = useCallback(natures => {
        return producerIds.reduce((acc, id) => {
            const {
                [id]: listOperations = [],
            } = groupOperationsProducer
            const listAnalysis = listOperations.flatMap(o => groupAnalysis[`${o.qualitometer}:${o.id}`] || [])
            acc[`${id}`] = calculateDataAnalysis(listAnalysis, natures, parametersIndex)
            return acc
        }, {})
    }, [producerIds, groupOperationsProducer, groupAnalysis, parametersIndex])

    const microbioDataAnalysis = useMemo(() => {
        return formatDataAnalysis(NATURES_MICROBIO)
    }, [formatDataAnalysis])

    const pcDataAnalysis = useMemo(() => {
        return formatDataAnalysis(NATURES_PC)
    }, [formatDataAnalysis])

    return (
        <Grid container>
            <Grid size={12}>
                <Card title={i18n.nbSample}>
                    <div className={'scrollable-card'}>
                        <CustomTable
                            label={i18n.plv}
                            producerIds={producerIds}
                            microbioData={microbioDataSample}
                            pcData={pcDataSample}
                            referenceThreshold={filter.referenceThreshold}
                            limitThreshold={filter.limitThreshold}
                        />
                    </div>
                </Card>
            </Grid>
            <Grid size={12}>
                <Card title={i18n.nbAnalysis}>
                    <div className={'scrollable-card'}>
                        <CustomTable
                            label={i18n.analysis}
                            producerIds={producerIds}
                            microbioData={microbioDataAnalysis}
                            pcData={pcDataAnalysis}
                            referenceThreshold={filter.referenceThreshold}
                            limitThreshold={filter.limitThreshold}
                        />
                    </div>
                </Card>
            </Grid>
        </Grid>
    )
}

// Tableau du bilan conformité à partir du résultat calculé côté back (route distributionUnit/conformity) :
// une ligne par champ et par producteur, une colonne par nature (bactériologique, physico-chimique) et une colonne total

// fond des totaux (colonne total et taux de la ligne total)
const TOTAL_STYLE = { backgroundColor: lightBlue, fontWeight: 'bold' }
const PRODUCER_MAX_LENGTH = 40

// nom de l'exploitant tronqué au-delà de 40 caractères (nom complet en infobulle)
const getProducerName = name => name.length > PRODUCER_MAX_LENGTH ? `${name.slice(0, PRODUCER_MAX_LENGTH)}…` : name

const ConformityOverviewTables = ({
    result = {},
}) => {
    const producers = useSelector(store => store.ContributorReducer.producers)

    const rows = useMemo(() => getConformityOverviewRows(result), [result])

    // valeur colorée : en pastille sur les cases de total (fond bleu clair), en texte coloré sinon
    const getCell = (value, isTotal, color) => (
        <td className='center-align aepTableCell' style={{ ...(isTotal ? TOTAL_STYLE : {}), ...(color && !isTotal ? { color, fontWeight: 'bold' } : {}) }}>
            {color && isTotal ? (
                <span style={{ backgroundColor: color, color: 'white', borderRadius: 4, padding: '0.1rem 0.5rem' }}>{value}</span>
            ) : value}
        </td>
    )

    // les champs, toujours dans le même ordre ; les non conformités apparaissent en rouge
    const fields = [
        { label: i18n.conformity, cell: (stats, isTotal) => getCell(stats.percent, isTotal, stats.color) },
        { label: i18n.nbSample, cell: (stats, isTotal) => getCell(stats.nbSample, isTotal) },
        { label: i18n.analysis, cell: (stats, isTotal) => getCell(stats.nbAnalysis, isTotal) },
        { label: i18n.nonConformities, cell: (stats, isTotal) => getCell(stats.nonConform, isTotal, stats.nonConform ? CONFORMITY_COLORS.nonConform : undefined) },
        { label: i18n.referenceExceedance, cell: (stats, isTotal) => getCell(stats.ref, isTotal) },
    ]

    return (
        <div className='scrollable-card'>
            <table>
                <thead>
                    <tr>
                        <th className='aepTableCell' colSpan={2} />
                        <th className='center-align aepTableCell'>{i18n.microbiological}</th>
                        <th className='center-align aepTableCell'>{i18n.physicochemic}</th>
                        <th className='center-align aepTableCell'>{i18n.total}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.producers.map(({ producer, microbiological, physicochemical, total }) => fields.map((field, index) => {
                        const producerName = getLabel(producers, producer, 'mnemonique', 'id') || i18n.unknownProducer
                        return (
                            <tr key={`${producer}_${index}`}>
                                {index === 0 && (
                                    <td rowSpan={fields.length} className='center-align aepTableCell' style={{ width: '25%', fontWeight: 'bold' }} title={producerName}>
                                        {getProducerName(producerName)}
                                    </td>
                                )}
                                <td className='aepTableCell padding-left-1' style={{ fontWeight: 'bold' }}>{field.label}</td>
                                {field.cell(microbiological, false)}
                                {field.cell(physicochemical, false)}
                                {field.cell(total, true)}
                            </tr>
                        )
                    }))}
                    <tr style={{ height: '2.5rem' }}>
                        <td className='center-align aepTableCell' style={{ fontWeight: 'bold' }}>{i18n.total}</td>
                        <td className='aepTableCell' />
                        {getCell(rows.total.microbiological.percent, true, rows.total.microbiological.color)}
                        {getCell(rows.total.physicochemical.percent, true, rows.total.physicochemical.color)}
                        {getCell(rows.total.total.percent, true, rows.total.total.color)}
                    </tr>
                </tbody>
            </table>
        </div>
    )
}

ConformityOverviewTables.propTypes = {
    result: PropTypes.shape({
        nbAnalysis: PropTypes.number,
        producers: PropTypes.arrayOf(PropTypes.shape({})),
    }),
}

// Onglet "Bilan" d'une UDI, calculé côté back
const AEPConformityOverviewPanel = ({
    filter = {},
    onExport = () => { },
    result = {},
}) => {
    const producers = useSelector(store => store.ContributorReducer.producers)

    useActions(() => ({
        exportList: [{
            onClick: () => {
                const data = getConformityOverviewExportRows(result, filter, producers)
                onExport({
                    data: data.length ? [{ ...data[0], headers: CONFORMITY_OVERVIEW_EXPORT_HEADERS }, ...data.slice(1)] : [],
                    exportType: 'xlsx',
                    titleFile: i18n.AEPoverview,
                })
            },
            label: i18n.excel,
        }],
    }), [result, filter, producers, onExport])

    return <ConformityOverviewTables result={result} filter={filter} />
}

AEPConformityOverviewPanel.propTypes = {
    filter: PropTypes.shape({
        year: PropTypes.number,
    }),
    onExport: PropTypes.func,
    result: PropTypes.shape({}),
}

AEPoverviewPanel.propTypes = {
    filter: PropTypes.shape({
        referenceThreshold: PropTypes.number,
        limitThreshold: PropTypes.number,
        year: PropTypes.number,
        producer: PropTypes.number,
    }),
    onExport: PropTypes.func,
    thresholdList: PropTypes.arrayOf(PropTypes.instanceOf(DtoQualityThreshold)),
    operations: PropTypes.arrayOf(PropTypes.instanceOf(DtoOperation)),
    analysis: PropTypes.arrayOf(PropTypes.shape({
        // DtoAnalysisLight
        // calculateThresholdsResult
        producer: PropTypes.number,
    })),
}

export default AEPoverviewPanel
export {
    ConformityOverviewTables,
    AEPConformityOverviewPanel,
}